import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface TechItem {
  name: string;
  icon: string;
  category: "frontend" | "backend" | "database" | "devops";
}

const techStack: TechItem[] = [
  // Frontend
  { name: "React", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg", category: "frontend" },
  { name: "Next.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg", category: "frontend" },
  { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg", category: "frontend" },
  { name: "Tailwind", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", category: "frontend" },
  { name: "Vue", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg", category: "frontend" },

  // Backend
  { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg", category: "backend" },
  { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg", category: "backend" },
  { name: "Express", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg", category: "backend" },
  { name: "FastAPI", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastapi/fastapi-original.svg", category: "backend" },

  // Database
  { name: "PostgreSQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg", category: "database" },
  { name: "Supabase", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/supabase/supabase-original.svg", category: "database" },
  { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg", category: "database" },
  { name: "Redis", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg", category: "database" },

  // DevOps
  { name: "Docker", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg", category: "devops" },
  { name: "AWS", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", category: "devops" },
  { name: "Git", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg", category: "devops" },
  { name: "Vercel", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vercel/vercel-original.svg", category: "devops" },
];

interface TechStackGridProps {
  className?: string;
  showCategories?: boolean;
}

export const TechStackGrid = ({ className, showCategories = false }: TechStackGridProps) => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (showCategories) {
    const categories = {
      frontend: techStack.filter((t) => t.category === "frontend"),
      backend: techStack.filter((t) => t.category === "backend"),
      database: techStack.filter((t) => t.category === "database"),
      devops: techStack.filter((t) => t.category === "devops"),
    };

    return (
      <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8", className)}>
        {Object.entries(categories).map(([category, items]) => (
          <div key={category} className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {category === "frontend" && "Frontend"}
              {category === "backend" && "Backend"}
              {category === "database" && "Database"}
              {category === "devops" && "DevOps"}
            </h4>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex flex-wrap gap-3"
            >
              {items.map((tech) => (
                <motion.div
                  key={tech.name}
                  variants={item}
                  whileHover={{ scale: 1.1, y: -5 }}
                  className="group relative flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <img
                    src={tech.icon}
                    alt={tech.name}
                    className="w-5 h-5 object-contain"
                  />
                  <span className="text-sm text-foreground">{tech.name}</span>
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-600/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className={cn("flex flex-wrap justify-center gap-4", className)}
    >
      {techStack.map((tech) => (
        <motion.div
          key={tech.name}
          variants={item}
          whileHover={{ scale: 1.1, y: -5 }}
          className="group relative flex items-center gap-2 px-4 py-3 bg-card rounded-xl border border-border hover:border-primary/50 transition-all hover:shadow-lg hover:shadow-primary/10"
        >
          <img
            src={tech.icon}
            alt={tech.name}
            className="w-6 h-6 object-contain"
          />
          <span className="text-sm font-medium text-foreground">{tech.name}</span>
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
        </motion.div>
      ))}
    </motion.div>
  );
};

export default TechStackGrid;
